import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { fetchJson, assetUrl } from "../lib/api";
import { FIGHTER_STATUSES, FIGHTER_GENDERS } from "../lib/constants";
import { nationalityLabel, findCountry } from "../lib/countries";
import FighterImage from "../components/FighterImage";
import StatusBadge from "../components/StatusBadge";

const ALL = "__ALL__";

export default function Fighters() {
  const [fighters, setFighters] = useState([]);
  // Phase B — pool complet chargé une seule fois pour construire la liste
  // des nationalités disponibles. Il reste stable quand on filtre par pays
  // (sinon on aurait le comportement pénible d'une liste qui se vide
  // dès qu'on sélectionne un pays qui filtre tous les autres).
  const [rosterPool, setRosterPool] = useState([]);
  const [weightClasses, setWeightClasses] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [q, setQ] = useState("");
  const [wc, setWc] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [academy, setAcademy] = useState(ALL);
  const [gender, setGender] = useState(ALL);
  const [country, setCountry] = useState(ALL);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [wcs, acs, pool] = await Promise.all([
          fetchJson("/weight-classes"),
          fetchJson("/academies"),
          fetchJson("/fighters", { limit: 200 }),
        ]);
        setWeightClasses(wcs || []);
        setAcademies(acs || []);
        setRosterPool(pool || []);
      } catch (_e) {
        // filter data optional, ignore
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = { limit: 200 };
      if (q) params.q = q;
      if (wc !== ALL) params.weight_class_id = wc;
      if (status !== ALL) params.status = status;
      if (academy !== ALL) params.academy_id = academy;
      if (gender !== ALL) params.gender = gender;
      if (country !== ALL) params.nationality = country;
      try {
        const data = await fetchJson("/fighters", params);
        setFighters(data || []);
      } catch (_) {
        setFighters([]);
      }
      setLoading(false);
    })();
  }, [q, wc, status, academy, gender, country]);

  const wcOptions = useMemo(
    () => weightClasses.filter((w) => (w.league_id ? true : true)),
    [weightClasses],
  );

  // Phase B — liste des pays disponibles construite depuis les fighters
  // réellement en base. Ordre : Burkina Faso en premier (ligue nationale),
  // le reste alphabétique en français. On accepte aussi bien un code ISO2
  // (canonique) qu'un nom déjà stocké en clair (données legacy).
  const countryOptions = useMemo(() => {
    const uniq = new Map(); // code → {code, name, flag}
    for (const f of rosterPool) {
      const raw = (f.nationality || "").trim();
      if (!raw) continue;
      // Résolution du code ISO2 : si `raw` est déjà un code (ex: "BF"),
      // on le garde ; sinon on tente de retrouver l'entrée par nom.
      let entry = findCountry(raw.toUpperCase());
      if (!entry) {
        const byName = findCountry(raw); // no-op si raw n'est pas un code
        entry = byName || null;
      }
      const code = entry ? entry.code : raw.toUpperCase();
      if (uniq.has(code)) continue;
      uniq.set(code, {
        code,
        name: entry ? entry.name : (nationalityLabel(raw) || raw),
        flag: entry ? entry.flag : (f.nationality_flag || ""),
      });
    }
    const arr = Array.from(uniq.values());
    arr.sort((a, b) => {
      if (a.code === "BF") return -1;
      if (b.code === "BF") return 1;
      return a.name.localeCompare(b.name, "fr");
    });
    return arr;
  }, [rosterPool]);

  const resetAll = () => {
    setQ("");
    setWc(ALL);
    setStatus(ALL);
    setAcademy(ALL);
    setGender(ALL);
    setCountry(ALL);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8" data-testid="fighters-page">
      {/* Header */}
      <div className="pt-14 md:pt-20 pb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
          Base officielle BUFL
        </p>
        <h1 className="mt-4 font-display font-black text-6xl md:text-9xl leading-[0.85] text-white">
          COMBATTANTS
        </h1>
        <p className="mt-4 text-neutral-500 text-sm md:text-base max-w-2xl">
          Recherche par nom, surnom, ville ou catégorie.
          Base réservée aux combattants officiels de la ligue.
        </p>
      </div>

      {/* Filters */}
      <div className="border-y border-neutral-900 py-4 md:py-6 sticky top-14 md:top-16 z-30 bg-black/85 backdrop-blur">
        <div className="flex flex-col md:flex-row gap-3 md:gap-4">
          <div className="relative flex-1">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-600"
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="fighters-search-input"
              placeholder="Rechercher un combattant…"
              className="w-full bg-neutral-950 border border-neutral-900 pl-10 pr-4 h-11 text-sm text-white uppercase tracking-widest font-stats placeholder:text-neutral-700 focus:border-gold focus:outline-none transition-colors duration-200"
            />
          </div>

          <select
            value={wc}
            onChange={(e) => setWc(e.target.value)}
            data-testid="fighters-filter-weight"
            className="bg-neutral-950 border border-neutral-900 h-11 px-3 text-xs text-white uppercase tracking-widest font-stats focus:border-gold outline-none min-w-[180px]"
          >
            <option value={ALL}>Toutes catégories</option>
            {wcOptions.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="fighters-filter-status"
            className="bg-neutral-950 border border-neutral-900 h-11 px-3 text-xs text-white uppercase tracking-widest font-stats focus:border-gold outline-none min-w-[160px]"
          >
            <option value={ALL}>Tous statuts</option>
            {Object.entries(FIGHTER_STATUSES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>

          <select
            value={academy}
            onChange={(e) => setAcademy(e.target.value)}
            data-testid="fighters-filter-academy"
            className="bg-neutral-950 border border-neutral-900 h-11 px-3 text-xs text-white uppercase tracking-widest font-stats focus:border-gold outline-none min-w-[180px]"
          >
            <option value={ALL}>Toutes académies</option>
            {academies.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>

          <select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            data-testid="fighters-filter-gender"
            className="bg-neutral-950 border border-neutral-900 h-11 px-3 text-xs text-white uppercase tracking-widest font-stats focus:border-gold outline-none min-w-[130px]"
          >
            <option value={ALL}>Tous genres</option>
            {Object.entries(FIGHTER_GENDERS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>

          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            data-testid="fighters-filter-country"
            className="bg-neutral-950 border border-neutral-900 h-11 px-3 text-xs text-white uppercase tracking-widest font-stats focus:border-gold outline-none min-w-[160px]"
          >
            <option value={ALL}>Tous les pays</option>
            {countryOptions.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag ? `${c.flag} ${c.name}` : c.name}
              </option>
            ))}
          </select>

          {(q || wc !== ALL || status !== ALL || academy !== ALL || gender !== ALL || country !== ALL) && (
            <button
              onClick={resetAll}
              data-testid="fighters-reset"
              className="inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest text-neutral-500 hover:text-white transition-colors duration-200 px-3"
            >
              <X size={12} /> Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="py-10">
        <p className="text-xs font-stats uppercase tracking-widest text-neutral-600 mb-6">
          {loading
            ? "Chargement…"
            : `${fighters.length} combattant${fighters.length > 1 ? "s" : ""} trouvé${fighters.length > 1 ? "s" : ""}`}
        </p>

        {fighters.length === 0 && !loading ? (
          <div className="border border-neutral-900 line-grid py-24 px-8 text-center">
            <p className="font-display font-black text-3xl md:text-5xl text-neutral-600 leading-none">
              AUCUN COMBATTANT
            </p>
            <p className="mt-3 text-neutral-500 text-sm">
              Ajustez vos filtres pour élargir la recherche.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {fighters.map((f, idx) => (
              <Link
                key={f.id}
                to={`/fighters/${f.slug}`}
                data-testid={`fighter-card-${f.slug}`}
                className={`group relative border border-neutral-900 bg-neutral-950 aspect-[3/4] flex flex-col justify-end overflow-hidden ${
                  idx % 3 === 1 ? "md:mt-8" : ""
                }`}
              >
                <FighterImage
                  src={assetUrl(f.photo_url)}
                  alt={f.first_name}
                  className="absolute inset-0"
                  imgClassName="object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
                <div className="absolute top-3 right-3">
                  <StatusBadge status={f.status} />
                </div>
                <div className="relative p-4 md:p-5">
                  {(f.nationality_flag || f.city) && (
                    <p className="text-[10px] font-stats uppercase tracking-widest text-neutral-500">
                      {[f.nationality_flag, f.city].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  <h3 className="mt-1 font-display font-black text-3xl md:text-4xl text-white leading-none uppercase">
                    {f.first_name}
                    {f.last_name && (
                      <>
                        {" "}
                        <span className="text-neutral-500 font-black">{f.last_name}</span>
                      </>
                    )}
                  </h3>
                  {f.nickname && (
                    <p className="mt-1 text-gold italic text-xs md:text-sm">
                      &laquo;&nbsp;{f.nickname}&nbsp;&raquo;
                    </p>
                  )}
                  <p className="mt-3 font-stats text-[10px] md:text-xs text-neutral-400 uppercase tracking-widest">
                    {[
                      f.display_record?.record_str || f.record?.record_str,
                      f.current_weight_kg ? `${f.current_weight_kg} KG` : null,
                      f.weight_class?.name?.split(" (")[0],
                    ].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
